import { DateTimePicker, Label } from "@tellescope/ui";
import { cn, maskDateInput } from "@tellescope/utils";
import { TellescopeFormFieldBaseProps } from "../types";
import { dateFromOffsetMs } from "../utils";
import { FieldDescription } from "../FieldDescription";
import { useMaskedDateInput } from "./useMaskedDateInput";
import { MaskedDateHelperText } from "./MaskedDateHelperText";

export interface DateFieldProps extends TellescopeFormFieldBaseProps<"date"> {}

const maskFromValue = (value: Date | undefined) => {
  if (!value) return "";
  const hours = value.getHours();
  const meridiem = hours < 12 ? "a" : "p";
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  const digits = [
    String(value.getMonth() + 1).padStart(2, "0"),
    String(value.getDate()).padStart(2, "0"),
    String(value.getFullYear()),
    String(hour12).padStart(2, "0"),
    String(value.getMinutes()).padStart(2, "0"),
  ].join("");
  return maskDateInput(`${digits}${meridiem}`, { includeTime: true });
};

export const DateField = ({
  title,
  description,
  htmlDescription,
  placeholder,
  value,
  onChange,
  className,
  error,
  onBlur,
  touched,
  required,
  options,
  autoFocus,
  disabled,
}: DateFieldProps) => {
  const showError = !!(error && touched);

  const minDate = options?.minDateOffsetMs !== undefined ? dateFromOffsetMs(options.minDateOffsetMs) : undefined;
  const maxDate = options?.maxDateOffsetMs !== undefined ? dateFromOffsetMs(options.maxDateOffsetMs) : undefined;
  const disabledDates = [...(minDate ? [{ before: minDate }] : []), ...(maxDate ? [{ after: maxDate }] : [])];

  const {
    value: maskedValue,
    parsed,
    ...maskedInputProps
  } = useMaskedDateInput({
    maskedValue: maskFromValue(value),
    includeTime: true,
    onCommit: (date) => onChange(date ?? undefined),
  });

  return (
    <div className="space-y-1">
      {!!(title || placeholder) && <Label required={required}>{title || placeholder}</Label>}
      <FieldDescription description={description} htmlDescription={htmlDescription} />
      <DateTimePicker
        value={value}
        onChange={onChange}
        onClose={onBlur}
        className={cn(className, showError && "border-destructive")}
        label=""
        placeholder={placeholder ?? "MM-DD-YYYY hh:mm AM/PM"}
        disabled={disabled}
        disabledDates={disabledDates.length > 0 ? disabledDates : undefined}
        defaultOpen={autoFocus}
        inputProps={{ value: maskedValue, ...maskedInputProps }}
      />
      <MaskedDateHelperText value={parsed} includeTime />
      {showError && <p className="text-sm text-destructive mt-1">{error}</p>}
    </div>
  );
};
